import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerMobileSidebar from '../components/ManagerMobileSidebar';
import ManagerHeader from '../components/ManagerHeader';
import Footer from '../components/Footer';
import { authAPI } from '../services/api';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface UserProfile {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login: string;
  created_at: string;
  permissions: string[];
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  profile_picture: string;
  date_of_birth: string;
  gender: string;
  business_name: string;
  business_type: string;
  pan_number: string;
  gst_number: string;
  bank_account_number: string;
  ifsc_code: string;
  bank_name: string;
  account_holder_name: string;
  app_url: string;
  accepting_payments: string;
  ip_address: string;
  services: string;
  client_id?: string;
  api_key: string;
  client_secret?: string;
  webhook_url: string;
}

const ManagerAccount: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor } = useManagerTheme();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<UserProfile>>({});
  const [saving, setSaving] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const loadingStartRef = useRef(0);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      const response = await authAPI.getProfile();
      setProfile(response.data.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError('Failed to fetch profile data');
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPermissions = (permissions: string[]) => {
    if (!permissions || permissions.length === 0) return 'No specific permissions';
    return permissions.join(', ');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleEdit = () => {
    if (profile) {
      setEditData({
        name: profile.name,
        phone: profile.phone,
        address: profile.address,
        city: profile.city,
        state: profile.state,
        country: profile.country,
        pincode: profile.pincode,
        date_of_birth: profile.date_of_birth,
        gender: profile.gender,
        business_name: profile.business_name,
        business_type: profile.business_type,
        pan_number: profile.pan_number,
        gst_number: profile.gst_number,
        bank_account_number: profile.bank_account_number,
        ifsc_code: profile.ifsc_code,
        bank_name: profile.bank_name,
        account_holder_name: profile.account_holder_name,
        app_url: profile.app_url,
        accepting_payments: profile.accepting_payments,
        ip_address: profile.ip_address,
        services: profile.services,
        webhook_url: profile.webhook_url
      });
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare data for API (include all editable fields)
      const updateData = {
        // Personal Information
        name: editData.name,
        phone: editData.phone,
        address: editData.address,
        city: editData.city,
        state: editData.state,
        country: editData.country,
        pincode: editData.pincode,
        date_of_birth: editData.date_of_birth,
        gender: editData.gender,
        
        // Business Information
        business_name: editData.business_name,
        business_type: editData.business_type,
        pan_number: editData.pan_number,
        gst_number: editData.gst_number,
        bank_name: editData.bank_name,
        account_holder_name: editData.account_holder_name,
        bank_account_number: editData.bank_account_number,
        ifsc_code: editData.ifsc_code,
        
        // Security & API Information
        webhook_url: editData.webhook_url,
        ip_address: editData.ip_address,
        app_url: editData.app_url,
        services: editData.services
      };

      await authAPI.updateProfile(updateData);
      
      // Refresh profile data
      await fetchProfile();
      
      setIsEditing(false);
      setEditData({});
      setError(null);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderEditableField = (field: string, label: string, type: string = 'text') => {
    const value = isEditing ? (editData[field as keyof UserProfile] || '') : (profile?.[field as keyof UserProfile] || '');
    const stringValue = String(value || '');
    
    if (isEditing) {
      return (
        <div className="flex flex-col gap-1 mb-3">
          <label>{label}</label>
          <input
            type={type}
            value={stringValue}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        </div>
      );
    }
    
    return (
      <div className="flex flex-col gap-1 mb-3">
        <label>{label}</label>
        <div className="text-slate-200 font-medium">{stringValue || 'Not provided'}</div>
      </div>
    );
  };

  const layoutWrapper = 'flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200';
  const mainContent = 'flex flex-1';
  const accountContent = 'flex-1 flex flex-col p-6 md:p-8 text-slate-200';

  if (loading) {
    return (
      <div className={layoutWrapper} style={{ backgroundColor: contentColor }}>
        <ManagerHeader user={user} onRefresh={fetchProfile} onCreditMoney={() => console.log('Credit Money')} onMyProfile={() => window.location.href = '/manager/account'} onLogout={handleLogout} />
        <div className={mainContent}>
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="account" /></div>
          <div className="flex-1 flex flex-col backdrop-blur-sm p-6 md:p-8 text-slate-200" style={{ backgroundColor: contentColor }}>
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-4" />
              <p className="text-slate-300 m-0">Loading profile data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={layoutWrapper} style={{ backgroundColor: contentColor }}>
        <ManagerHeader user={user} onRefresh={fetchProfile} onCreditMoney={() => console.log('Credit Money')} onMyProfile={() => window.location.href = '/manager/account'} onLogout={handleLogout} />
        <div className={mainContent}>
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="account" /></div>
          <div className={accountContent} style={{ backgroundColor: contentColor }}>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold text-slate-100 m-0 mb-2">Error Loading Profile</h3>
              <p className="text-slate-300 m-0 mb-4">{error}</p>
              <button type="button" onClick={fetchProfile} className="py-2.5 px-5 rounded-lg font-semibold text-sm bg-gradient-to-r from-blue-500 to-violet-500 text-white border-none cursor-pointer hover:-translate-y-0.5 hover:shadow-lg">Try Again</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutWrapper} style={{ backgroundColor: contentColor }}>
      <ManagerMobileSidebar showMobileSidebar={showMobileSidebar} setShowMobileSidebar={setShowMobileSidebar} activeTab="account" onCreditMoney={() => console.log('Credit Money')} onLogout={handleLogout} />
      <ManagerHeader user={user} onRefresh={fetchProfile} onCreditMoney={() => console.log('Credit Money')} onMyProfile={() => window.location.href = '/manager/account'} onLogout={handleLogout} />
      <div className={mainContent}>
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="account" /></div>
        <div className={accountContent} style={{ backgroundColor: contentColor }}>
          <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl shadow-black/25 mb-6" style={{ backgroundColor: sectionColor }}>
            <div className="flex justify-between items-start gap-8">
              <div className="flex-1">
                <h1 className="text-xl font-bold text-slate-100 m-0 mb-1">My Account</h1>
                <p className="text-slate-400 text-sm m-0 font-medium">Manage your account information and settings</p>
              </div>
              <div className="flex items-center gap-4">
                {!isEditing ? (
                  <button type="button" onClick={handleEdit} className="flex items-center gap-2 py-3 px-5 rounded-lg bg-gradient-to-r from-blue-500 to-violet-500 text-white border-none text-sm font-semibold cursor-pointer hover:-translate-y-0.5 hover:shadow-lg">✏️ Edit Profile</button>
                ) : (
                  <div className="flex gap-3">
                    <button type="button" onClick={handleCancel} className="py-2.5 px-5 rounded-lg font-semibold text-sm bg-white/10 text-slate-200 border border-white/20 cursor-pointer hover:bg-white/20 disabled:opacity-50" disabled={saving}>Cancel</button>
                    <button type="button" onClick={handleSave} className="py-2.5 px-5 rounded-lg font-semibold text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-none cursor-pointer hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2 mb-6 flex-wrap">
            {(['account', 'personal', 'business', 'security'] as const).map((section) => (
              <button
                key={section}
                type="button"
                className={`py-2.5 px-4 rounded-lg text-sm font-medium cursor-pointer transition-all ${
                  activeSection === section ? 'bg-violet-500/30 text-white border border-violet-400' : 'bg-white/10 text-slate-300 border border-white/10 hover:bg-white/20'
                }`}
                onClick={() => setActiveSection(section)}
              >
                {section === 'account' && '🔐 Account Details'}
                {section === 'personal' && '🔒 Security & API Info'}
                {section === 'business' && '🏢 Business Info'}
                {section === 'security' && '🔒 Security & API'}
              </button>
            ))}
          </div>

          <div className="flex-1 flex flex-col gap-6">
            {activeSection === 'personal' && (
              <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl shadow-black/25 mb-6" style={{ backgroundColor: sectionColor }}>
                <h2>Security & API Information</h2>
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    {/* Client ID - Read Only */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">CLIENT ID</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        <div className="info-value api-key">
                          <span className="api-value-text">{profile?.client_id || 'Not set'}</span>
                          {profile?.client_id && (
                            <button 
                              className="ml-2 py-1 px-2 rounded bg-white/10 text-slate-200 cursor-pointer hover:bg-white/20 border-none text-sm"
                              onClick={() => navigator.clipboard.writeText(profile.client_id as string)}
                              title="Copy Client ID"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* API Key - Read Only for Security */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">API KEY</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        <div className="info-value api-key">
                          <span className="api-value-text">{profile?.api_key || 'Not generated'}</span>
                          {profile?.api_key && (
                            <button 
                              className="ml-2 py-1 px-2 rounded bg-white/10 text-slate-200 cursor-pointer hover:bg-white/20 border-none text-sm"
                              onClick={() => navigator.clipboard.writeText(profile.api_key)}
                              title="Copy API Key"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Client Secret - Read Only for Security */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">CLIENT SECRET</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        <div className="info-value secret-key">
                          <span className="api-value-text">{profile?.client_secret ? '••••••••••••••••' : 'Not generated'}</span>
                          {profile?.client_secret && (
                            <button 
                              className="ml-2 py-1 px-2 rounded bg-white/10 text-slate-200 cursor-pointer hover:bg-white/20 border-none text-sm"
                              onClick={() => navigator.clipboard.writeText(profile.client_secret as string)}
                              title="Copy Client Secret"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Webhook URL */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">WEBHOOK URL</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.webhook_url || ''}
                            onChange={(e) => handleInputChange('webhook_url', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Webhook URL"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.webhook_url || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    {/* IP Address */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">IP ADDRESS</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.ip_address || ''}
                            onChange={(e) => handleInputChange('ip_address', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter IP Address"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.ip_address || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    {/* App URL */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">APP URL</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.app_url || ''}
                            onChange={(e) => handleInputChange('app_url', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter App URL"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.app_url || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    {/* Services */}
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">SERVICES</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.services || ''}
                            onChange={(e) => handleInputChange('services', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Services"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.services || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                {isEditing && (
                  <div className="mt-4 p-4 rounded-xl bg-amber-500/20 border border-amber-400/30 text-amber-200 text-sm">
                    <p>🔒 <strong>Security Note:</strong> API Key and Secret Key are read-only for security reasons. Contact support if you need to regenerate them.</p>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'account' && (
              <>
                {/* Account Details Section */}
                <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl shadow-black/25 mb-6" style={{ backgroundColor: sectionColor }}>
                  <h2>Account Details</h2>
                  <table className="w-full border-collapse text-sm">
                    <tbody>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">USER ID</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className="text-slate-200 font-medium">#{profile?.id}</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">ROLE</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className="info-value role-badge">{profile?.role}</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">ACCOUNT STATUS</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className={`inline-block py-1 px-2 rounded-lg text-sm font-semibold ${profile?.is_active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-red-500/30 text-red-200'}`}>
                            {profile?.is_active ? 'Active' : 'Inactive'}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">MEMBER SINCE</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className="text-slate-200 font-medium">{formatDate(profile?.created_at || '')}</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">LAST LOGIN</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className="text-slate-200 font-medium">{formatDate(profile?.last_login || '')}</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">PERMISSIONS</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className="info-value permissions">{formatPermissions(profile?.permissions || [])}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Personal Information Section */}
                <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl shadow-black/25 mb-6" style={{ backgroundColor: sectionColor }}>
                  <h2>Personal Information</h2>
                  <table className="w-full border-collapse text-sm">
                    <tbody>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">FULL NAME</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.name || ''}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter Full Name"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.name || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">EMAIL ADDRESS</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          <div className="info-value email-field">{profile?.email || 'Not provided'}</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">PHONE NUMBER</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="tel"
                              value={editData.phone || ''}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter Phone Number"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.phone || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">DATE OF BIRTH</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="date"
                              value={editData.date_of_birth || ''}
                              onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter Date of Birth"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString('en-IN') : 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">GENDER</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.gender || ''}
                              onChange={(e) => handleInputChange('gender', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter Gender"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.gender || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">ADDRESS</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.address || ''}
                              onChange={(e) => handleInputChange('address', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter Address"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.address || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">CITY</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.city || ''}
                              onChange={(e) => handleInputChange('city', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter City"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.city || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">STATE</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.state || ''}
                              onChange={(e) => handleInputChange('state', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter State"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.state || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">COUNTRY</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.country || ''}
                              onChange={(e) => handleInputChange('country', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter Country"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.country || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">PIN CODE</td>
                        <td className="py-3 px-4 text-slate-200 align-top">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editData.pincode || ''}
                              onChange={(e) => handleInputChange('pincode', e.target.value)}
                              className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                              placeholder="Enter PIN Code"
                            />
                          ) : (
                            <div className="text-slate-200 font-medium">{profile?.pincode || 'Not provided'}</div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {isEditing && (
                    <div className="mt-4 p-4 rounded-xl bg-amber-500/20 border border-amber-400/30 text-amber-200 text-sm">
                      <p>💡 <strong>Note:</strong> Email address cannot be changed. Contact support if you need to update your email.</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeSection === 'business' && (
              <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl shadow-black/25 mb-6" style={{ backgroundColor: sectionColor }}>
                <h2>Business Information</h2>
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">BUSINESS NAME</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.business_name || ''}
                            onChange={(e) => handleInputChange('business_name', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Business Name"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.business_name || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">BUSINESS TYPE</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.business_type || ''}
                            onChange={(e) => handleInputChange('business_type', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Business Type"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.business_type || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">PAN NUMBER</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.pan_number || ''}
                            onChange={(e) => handleInputChange('pan_number', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter PAN Number"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.pan_number || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">GST NUMBER</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.gst_number || ''}
                            onChange={(e) => handleInputChange('gst_number', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter GST Number"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.gst_number || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">BANK NAME</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.bank_name || ''}
                            onChange={(e) => handleInputChange('bank_name', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Bank Name"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.bank_name || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">ACCOUNT HOLDER NAME</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.account_holder_name || ''}
                            onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Account Holder Name"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.account_holder_name || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">ACCOUNT NUMBER</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.bank_account_number || ''}
                            onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter Account Number"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.bank_account_number || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-400 font-semibold text-xs uppercase tracking-wider w-1/3 align-top">IFSC CODE</td>
                      <td className="py-3 px-4 text-slate-200 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.ifsc_code || ''}
                            onChange={(e) => handleInputChange('ifsc_code', e.target.value)}
                            className="w-full py-2 px-3 border-2 border-slate-500 rounded-lg text-slate-100 bg-slate-700/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20"
                            placeholder="Enter IFSC Code"
                          />
                        ) : (
                          <div className="text-slate-200 font-medium">{profile?.ifsc_code || 'Not provided'}</div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeSection === 'security' && (
              <div className="p-6 rounded-2xl border border-white/[0.06] shadow-xl shadow-black/25 mb-6" style={{ backgroundColor: sectionColor }}>
                <h2>Security & API Information</h2>
                <p style={{ color: '#64748b', margin: '1rem 0' }}>This section has been moved to the "Security & API Info" tab.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagerAccount;
